/**
 * WebflowOnboarding.js — the Webflow App onboarding, mounted at /webflow-register (the redirect URI registered in the
 * Webflow App, so the OAuth callback lands here).
 *
 * STRUCTURE AND STYLING ARE THE WIX ONBOARDING (WixOnboarding.js / wxo-* CSS), step for step (owner, 04.11.2026), in
 * the shape of its Stripe sibling WordpressRegister.js:
 *   0. Account      — create an account or log in (password or an e-mail code), visual on the right
 *   1. Knowledge    — the authorized Webflow site (picker when there are several), its website prefilled and
 *                     editable, documents; then the "looking at your site" radar while the agent is created and its
 *                     icon, colours, welcome and questions are prepared from the website (onboarding/prepare_brand)
 *   2. Design       — the widget with live preview and the suggested-question editor
 *   3. Test         — chat with the agent
 *   4. Add to site  — put the agent on the Webflow site (one deliberate click) and publish
 *   5. Notifications — events x channels matrix, the account e-mail prefilled
 *   6. Plan         — plan cards + 7-day free trial through the normal Stripe checkout, or the free plan
 *
 * WHAT DIFFERS FROM WIX (platform mechanics, not design):
 * - Entry is Webflow's OAuth callback (?code&state, moved out of the URL by public/index.html before anything runs);
 *   the code goes once into auth/webflow/check_account, the Webflow token stays on the server under a registration
 *   id, a reload resumes it (auth/webflow/resume), a refused or declined authorization shows its own screen whose
 *   button starts the authorization again (never automatically on a refresh).
 * - Accounts: auth/webflow/register|login|login_code, which attach the parked Webflow authorization to the account.
 * - The agent: webflow/onboarding/create_bot binds it to the chosen Webflow site from the first second; its website is
 *   learned in the background (find_new_links -> scrape_link) like Wix and WordPress.
 * - Billing is Stripe (create-checkout-session in a new tab, this tab polls the agent's plan), prices from
 *   /subscription_plans (monthly_price: $29/$59/$149, yearly $19/$39/$99 a month).
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TextField, Checkbox, IconButton, InputAdornment, Tooltip, MenuItem } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight, faArrowLeft, faArrowUp, faCheck, faXmark, faGlobe, faUpload, faTrash,
  faFileArrowUp, faFileLines, faPlus, faEllipsis, faHeadset, faEnvelope, faDesktop,
  faComments, faUserPlus, faTriangleExclamation, faBookOpen, faPuzzlePiece, faSliders,
  faMinus, faCode, faLink, faBolt, faCircleCheck, faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { faTelegram } from '@fortawesome/free-brands-svg-icons';
import ReactMarkdown from 'react-markdown';
import ChatModal from '../components/chatModal.js';
import WixAuthVisual, { GENERIC_ACTIONS, GENERIC_TOOLS } from '../components/WixAuthVisual.js';
import WixQuestionEditor from '../components/WixQuestionEditor';
import UploadFileModal from '../components/uploadFileModal.js';
import SlackLogo from '../components/SlackLogo.js';
import { SlackBrandIcon, TeamsBrandIcon, TelegramBrandIcon } from '../components/BrandIcons';
import { CHANNEL_GUIDES } from '../data/channelGuides.js';
import { saveInteraction } from '../components/Interaction';
import { SUPPORTED_LANGUAGES } from '../translations.js';
import { authFetch } from '../utils/api';
import { startWebflowConnect } from '../utils/webflowConnect';
import './WixOnboarding.css';
import './WebflowOnboarding.css';

const API = process.env.REACT_APP_API_BASE_URL;

const FIELD_SX = {
  backgroundColor: '#fff',
  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
  '& .MuiOutlinedInput-root.Mui-focused fieldset': { border: '1px solid #515151' },
  '& .MuiInputLabel-root': { color: 'gray' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#5616ea' },
};

const DEFAULT_WIDGET = {
  themeColor: '#5616ea',
  headerColor: '#ffffff',
  hoverColor: '#4413c4',
  widgetHorizontalAlignment: 'right',
  widgetSize: 62,
  widgetBorderRadius: 50,
  headerText: 'Chat with us',
  welcomeMessage: 'Hi, how can I assist you?',
  pulsing: true,
  icon: '',   // widget launcher icon (widget_icon_path)
  logo: '',   // logo shown in the chat header (header_icon_path)
  avatar: '', // avatar shown next to agent replies (avatar_icon_path)
  predefinedQuestions: [],
};

const TOTAL_STEPS = 7; // account, knowledge, design, test, add to site, notifications, plan
const STEP_DESIGN = 2;
const STEP_TEST = 3;
const STEP_ADD = 4;
const STEP_NOTIFY = 5;
const STEP_PLAN = 6;

// The radar shows at least this long, so the owner sees what is happening; the brand preparation runs meanwhile.
const SCAN_MIN_MS = 4500;
// Completion strips auto-dismiss this long after finishing, with a depleting ring.
const DONE_HIDE_MS = 5000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ── Notifications: events x channels matrix + e-mail recipients, the Wix onboarding's model. ──
const WFO_NOTIFY_CHANNELS = [
  { key: 'in_app', icon: faDesktop, labelKey: 'wxo_notif_ch_inapp' },
  { key: 'email', icon: faEnvelope, labelKey: 'wxo_notif_ch_email' },
];
const WFO_NOTIFY_EVENTS = [
  { key: 'new_chat', section: 'live_chat', icon: faComments, titleKey: 'wxo_notif_chat_title', descKey: 'wxo_notif_chat_desc' },
  { key: 'help_requested', section: 'live_chat', icon: faHeadset, titleKey: 'wxo_notif_help_title', descKey: 'wxo_notif_help_desc', highlight: true },
  { key: 'new_lead', section: 'leads', icon: faUserPlus, titleKey: 'wxo_notif_lead_title', descKey: 'wxo_notif_lead_desc' },
  { key: 'new_message', section: 'live_chat', icon: faEnvelope, titleKey: 'wxo_notif_msg_title', descKey: 'wxo_notif_msg_desc' },
];
const WFO_LOCKED_CHANNELS = { new_message: ['email'] }; // joined-chat replies: in-app only, no email spam
const wfoChannelLocked = (eventKey, channelKey) => (WFO_LOCKED_CHANNELS[eventKey] || []).includes(channelKey);
const WFO_DEFAULT_PREFS = {
  new_chat: { in_app: true, email: false },
  help_requested: { in_app: true, email: true },
  new_lead: { in_app: true, email: true },
  new_message: { in_app: true, email: false },
};

// ── Plan: everything shown (names, prices, limits, rescraping, branding, Stripe price id) comes from
//    /subscription_plans, like /billing; only the order and the featured tier live here. ──
const WFO_TIER_ORDER = ['Smart', 'Boost', 'Ultimo'];
const WFO_FEATURED_TIER = 'Boost';
const WFO_LOGOS_SHOWN = 5;
const WFO_APPS = [
  { key: 'calendly', label: 'Calendly', logo: '/calendly-logo.png' },
  { key: 'hubspot', label: 'HubSpot', logo: '/hubspot-logo.png' },
  { key: 'stripe', label: 'Stripe', logo: '/stripe-logo.png' },
  { key: 'mailchimp', label: 'Mailchimp', logo: '/mailchimp-logo.png' },
  { key: 'calcom', label: 'Cal.com', logo: '/calcom-logo.png' },
  { key: 'zendesk', label: 'Zendesk', logo: '/zendesk-logo.png' },
];
const WFO_PRIORITY_CHANNELS = [
  { key: 'instagram', label: 'Instagram', logo: '/instagram-logo.webp' },
  { key: 'facebook', label: 'Facebook', logo: '/facebook-logo.webp' },
  { key: 'whatsapp', label: 'WhatsApp', logo: '/whatsapp-logo.webp' },
  { key: 'slack', label: 'Slack', logoComponent: SlackLogo },
  { key: 'telegram', label: 'Telegram', icon: faTelegram, color: '#229ED9' },
  { key: 'website', labelKey: 'billing_channel_website_widget', icon: faCode },
  { key: 'sharelink', labelKey: 'billing_channel_chat_link', icon: faLink },
];
// Website builders — Webflow first here, since it's the owner's own platform.
const WFO_SITE_PLATFORMS = [
  { key: 'webflow', label: 'Webflow', logo: '/webflow-logo.png' },
  { key: 'wix', label: 'Wix', logo: '/wix-logo.png' },
  { key: 'wordpress', label: 'WordPress', logo: '/wordpress-logo.png' },
];
const WFO_GUIDE_CHANNELS = CHANNEL_GUIDES.map((g) => ({ key: g.key, label: g.name, logo: g.logo }));
const WFO_CHANNELS = [...WFO_PRIORITY_CHANNELS, ...WFO_SITE_PLATFORMS, ...WFO_GUIDE_CHANNELS];
const WFO_CHANNEL_ANY = { key: 'any-web', icon: faCode, labelKey: 'billing_chip_any_website', color: '#5616ea' };
const WFO_APP_ANY = { key: 'any-app', icon: faBolt, labelKey: 'billing_chip_any_app', color: '#5616ea' };
// The visual next to the account form: platform-neutral actions, Webflow first in the marquee.
const WFO_TOOLS = [{ src: '/webflow-logo.png', name: 'Webflow' }, ...GENERIC_TOOLS.filter((x) => x.name !== 'Webflow')];

function WfoLogoChip({ item }) {
  const { t } = useTranslation();
  const label = item.labelKey ? t(item.labelKey) : item.label;
  return (
    <span className="rk-logo" data-tip={label} aria-label={label}>
      {item.logoComponent ? (
        <item.logoComponent size={19} />
      ) : item.logo ? (
        <img src={item.logo} alt={label} />
      ) : (
        <FontAwesomeIcon icon={item.icon} style={item.color ? { color: item.color } : undefined} />
      )}
    </span>
  );
}

// A row of the most important logos with a "+N" chip whose popover lists the hidden ones.
function WfoLogoRow({ items, lead, max = WFO_LOGOS_SHOWN }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const shown = items.slice(0, max);
  const extra = items.length - max;
  const hidden = items.slice(max);
  const cancelClose = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  const scheduleClose = () => { cancelClose(); timer.current = setTimeout(() => setOpen(false), 90); };
  useEffect(() => () => cancelClose(), []);
  return (
    <div className="rk-logo-row">
      <div className="rk-logos">
        {lead && <WfoLogoChip key={lead.key} item={lead} />}
        {shown.map((it) => <WfoLogoChip key={it.key} item={it} />)}
        {extra > 0 && (
          <span className="rk-logo-more-wrap">
            <button type="button" className="rk-logo-more"
              onMouseEnter={() => { cancelClose(); setOpen(true); }} onMouseLeave={scheduleClose}
              onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label={t('billing_show_n_more', { count: extra })}>
              +{extra}
            </button>
            {open && (
              <div className="rk-logo-pop" role="tooltip" onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
                {hidden.map((it) => (
                  <span key={it.key} className="rk-logo-pop-item">
                    <WfoLogoChip item={it} />
                    <span className="rk-logo-pop-label">{it.labelKey ? t(it.labelKey) : it.label}</span>
                  </span>
                ))}
              </div>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

function base64ToFile(dataUrl, filename) {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8 = new Uint8Array(n);
  while (n--) u8[n] = bstr.charCodeAt(n);
  return new File([u8], filename, { type: mime });
}

// Webflow customers pay through Stripe: the plain monthly_price (wix_/shopify_ columns are those app markets').
function stripeDisplayPrice(plan) {
  return Number(plan?.monthly_price) || 0;
}

// Custom domains come host-only from Webflow; the field and the agent want a full address.
function siteHref(url) {
  const u = String(url || '').trim();
  if (!u) return '';
  return /^https?:\/\//i.test(u) ? u : `https://${u}`;
}
const siteHost = (url) => String(url || '').replace(/^https?:\/\//i, '').replace(/\/$/, '');

function domainName(input) {
  try {
    const u = new URL(siteHref(input));
    const host = u.hostname.replace(/^www\./, '');
    const parts = host.split('.');
    return parts.length >= 2 ? parts[parts.length - 2] : host;
  } catch { return 'agent'; }
}

// Any host-looking value: any TLD length, an optional scheme, port and path.
function isValidUrlSimple(value) {
  if (!value) return false;
  return /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d{2,5})?([/?#].*)?$/.test(value.trim());
}

// ── OAuth callback: one exchange per page load (Webflow Marketplace #1181064). React may mount this page twice in one
//    load; the second mount finds the address bar stripped and must neither lose the callback nor restart consent.
//    The code is never kept: it goes straight into the single check_account request. ──
let callbackExchange = null;

// Nothing to resume (30 minutes over, another browser, the address opened directly): the error screen, whose button
// starts the authorization. A reload never starts it by itself (no OAuth on page refresh).
function resumeAfterReload() {
  const nothingToResume = () => ({ ok: false, status: 0, j: { detail: 'nothing_to_resume' } });
  return fetch(`${API}auth/webflow/resume`, { method: 'POST' })
    .then(async (r) => {
      const j = r.ok ? await r.json().catch(() => ({})) : {};
      if (!r.ok || !j.registration_id) return nothingToResume();
      return { ok: true, status: r.status, j };
    })
    .catch(nothingToResume);
}

function exchangeCallback(code, state) {
  // code + state in the POST body, never in a query string; the backend verifies the state nonce it minted.
  return fetch(`${API}auth/webflow/check_account`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, state: state || null }),
  }).then(async (r) => ({ ok: r.ok, status: r.status, j: await r.json().catch(() => ({})) }));
}

// Any portal page opened from here on (the Stripe tab returns to /subscription-success in ANOTHER tab, the final
// "Go to the portal" in this one) opens on the agent this onboarding built, not on the account's last selection.
// localStorage is shared by the tabs of this origin; the event reaches an app shell already mounted in this tab.
function selectAgentInPortal(botId) {
  if (!botId) return;
  try {
    localStorage.setItem('selectedBotId', String(botId));
    localStorage.setItem('selectedBotIds', JSON.stringify([String(botId)]));
  } catch { /* storage blocked: the portal falls back to its own choice */ }
  window.dispatchEvent(new CustomEvent('ub:select-bot', { detail: { botId: String(botId) } }));
}

// A callback must carry our state; a stateless one starts the authorization again, once, through our install URL. A
// second one within two minutes gets the error screen instead of a loop.
const STATE_RESTART_KEY = 'ub_wf_state_restart';
const STATE_RESTART_WINDOW_MS = 120000;
function restartWithStateOnce() {
  try {
    const last = Number(sessionStorage.getItem(STATE_RESTART_KEY) || 0);
    if (Date.now() - last < STATE_RESTART_WINDOW_MS) {
      sessionStorage.removeItem(STATE_RESTART_KEY);
      return false;
    }
    sessionStorage.setItem(STATE_RESTART_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.replace(`${API}auth/webflow/connect`);
  return true;
}

export default function WebflowOnboarding({ onSetupComplete, onConnected }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLanguage = (i18n.language || 'en').split('-')[0];
  const currentLangLabel = (SUPPORTED_LANGUAGES.find((l) => l.code === currentLanguage) || { label: 'English' }).label;

  const [step, setStep] = useState(0);
  const [pageLoading, setPageLoading] = useState(true);
  const [callbackError, setCallbackError] = useState('');

  // account
  const [authMode, setAuthMode] = useState('register');
  const [registrationId, setRegistrationId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [signinCode, setSigninCode] = useState('');
  const [notice, setNotice] = useState('');
  const [uid, setUid] = useState(null);
  const [token, setToken] = useState(null);

  // knowledge: the authorized Webflow sites ([{ url, site_id, name }], one entry per domain)
  const [sites, setSites] = useState([]);
  const [siteKey, setSiteKey] = useState('');
  const [url, setUrl] = useState('');
  const [docFiles, setDocFiles] = useState([]);
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [docSync, setDocSync] = useState({ active: false, phase: 'idle', done: 0, total: 0 });
  const [bot, setBot] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanStep, setScanStep] = useState(0);

  // add to site: one click adds the script AND publishes the site ('' | 'adding' | 'publishing' while busy)
  const [addPhase, setAddPhase] = useState('');
  const [addError, setAddError] = useState('');
  const [liveOnSite, setLiveOnSite] = useState(false);
  const activatedRef = useRef(false);
  const addBusyRef = useRef(false);

  // notifications
  const [notifyPrefs, setNotifyPrefs] = useState(WFO_DEFAULT_PREFS);
  const [notificationEmails, setNotificationEmails] = useState(['']);
  const [notifySaveAttempted, setNotifySaveAttempted] = useState(false);
  const notifyEmailUserEditedRef = useRef(false);

  // plan (Stripe checkout, then poll the agent's plan)
  const [billingCycle, setBillingCycle] = useState('YEAR');
  const [isCheckingPlan, setIsCheckingPlan] = useState(false);
  const [planCheckError, setPlanCheckError] = useState('');
  const [trialStarted, setTrialStarted] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const planPollRef = useRef(null);

  // background website training + documents; the business context is generated once after both finished
  const [training, setTraining] = useState({ active: false, phase: 'idle', found: 0, done: 0 });
  const docKnowledgeRef = useRef(new Set());
  const trainingReqRef = useRef(null);
  const trainingKnowledgeRef = useRef(new Set());
  const trainedBotsRef = useRef(new Set());
  const bizContextFiredRef = useRef(false);
  const srcExpectedRef = useRef(new Set());
  const srcDoneRef = useRef(new Set());

  // design
  const [widget, setWidget] = useState(DEFAULT_WIDGET);
  const dirtyWidgetRef = useRef(new Set());
  const iconInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const avatarInputRef = useRef(null);
  const widgetFetchedRef = useRef(false);
  const [previewInput, setPreviewInput] = useState('');
  const [previewMsgs, setPreviewMsgs] = useState([]);
  const [previewMenuOpen, setPreviewMenuOpen] = useState(false);
  const [liveModalOpen, setLiveModalOpen] = useState(false);
  const previewBodyRef = useRef(null);
  const previewReplyRef = useRef(null);
  const previewMenuRef = useRef(null);

  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);
  const handleLanguageChange = (lang) => { i18n.changeLanguage(lang); localStorage.setItem('language', lang); };

  const site = useMemo(() => sites.find((s) => `${s.site_id}|${s.url}` === siteKey) || null, [sites, siteKey]);
  const siteId = site?.site_id || '';
  const siteName = site?.name || siteHost(site?.url);

  // ── scroll lock + ResizeObserver-loop guard (the embedded chat preview re-measures on mount) ──
  useEffect(() => {
    const html = document.documentElement, body = document.body;
    const ph = html.style.overflow, pb = body.style.overflow;
    html.style.overflow = 'hidden'; body.style.overflow = 'hidden';
    const Native = window.ResizeObserver;
    if (Native && !Native.__wxoWrapped) {
      const Wrapped = class extends Native {
        constructor(cb) {
          super((entries, obs) => { window.requestAnimationFrame(() => { try { cb(entries, obs); } catch (_) { /* noop */ } }); });
        }
      };
      Wrapped.__wxoWrapped = true;
      window.ResizeObserver = Wrapped;
    }
    return () => {
      html.style.overflow = ph; body.style.overflow = pb;
      if (Native) window.ResizeObserver = Native;
    };
  }, []);

  useEffect(() => {
    if (!langOpen) return;
    const onDown = (e) => { if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [langOpen]);

  const applySites = useCallback((list) => {
    const arr = Array.isArray(list) ? list.filter((x) => x && x.url && x.site_id) : [];
    if (!arr.length) return;
    setSites(arr);
    setSiteKey((k) => k || `${arr[0].site_id}|${arr[0].url}`);
    setUrl((u) => u || siteHref(arr[0].url));
  }, []);

  // ── entry: the OAuth callback (or a resume after a reload) ──
  useEffect(() => {
    const handed = window.__UB_WF_CALLBACK__ || null;
    try { delete window.__UB_WF_CALLBACK__; } catch { window.__UB_WF_CALLBACK__ = undefined; }
    const params = new URLSearchParams(window.location.search);
    const rawCode = handed ? handed.code : params.get('code');
    const rawState = handed ? handed.state : params.get('state');
    const rawError = handed ? handed.error : params.get('error');
    // ?code and ?state out of the address bar before any other network activity (Webflow #1180349).
    if (rawCode || rawState || rawError) {
      try { window.history.replaceState({}, '', window.location.pathname); } catch { /* non-fatal */ }
    }
    if (rawCode && !rawState) {
      if (restartWithStateOnce()) { callbackExchange = new Promise(() => {}); return undefined; }
      callbackExchange = Promise.resolve({ ok: false, status: 0, j: { detail: 'state_required' } });
    } else if (rawCode) {
      try { sessionStorage.removeItem(STATE_RESTART_KEY); } catch { /* non-fatal */ }
      callbackExchange = exchangeCallback(rawCode, rawState);
    } else if (rawError) {
      // Declined on Webflow's consent screen: say so and let them choose to authorize again (no loop).
      callbackExchange = Promise.resolve({ denied: true });
    } else if (!callbackExchange) {
      if (localStorage.getItem('token')) {
        // Signed in (a refresh after the exchange): the account exists, so the dashboard, not consent again.
        if (onConnected) onConnected('/overview');
        else navigate('/overview', { replace: true });
        return undefined;
      }
      callbackExchange = resumeAfterReload();
    }

    let alive = true;
    (async () => {
      try {
        const { ok, status, j = {}, denied } = await callbackExchange;
        if (!alive) return;
        if (denied) { setCallbackError(t('wf_oauth_denied')); return; }
        // "Connect existing agent" (started from the dashboard): the customer is signed in already.
        if (ok && (j.status === 'connected' || j.status === 'connected_and_activated')) {
          const rt = typeof j.return_to === 'string' && j.return_to.startsWith('/agents/new/') ? j.return_to : null;
          const activated = j.status === 'connected_and_activated';
          const to = rt || '/integration';
          const navState = rt
            ? { webflow: true, activated, needsPublish: !!j.needs_publish }
            : (activated ? { tab: 'webflow', activated: true, needsPublish: !!j.needs_publish } : { tab: 'webflow' });
          callbackExchange = null;
          if (onConnected) onConnected(to, navState);
          else navigate(to, { replace: true, state: navState });
          return;
        }
        if (!ok) {
          console.error('Webflow check_account failed:', status, j.detail);
          setCallbackError(t('wf_oauth_restart'));
          return;
        }
        // An existing portal account (same e-mail as the Webflow account) signs in; the e-mail match alone never does.
        if (j.has_account) {
          setAuthMode('login');
          setNotice(t('wf_account_exists_code', { email: j.user_email }));
        }
        setRegistrationId(j.registration_id || '');
        setUserEmail(j.user_email || '');
        applySites(j.urls);
        saveInteraction({ action: 'webflow_page_loaded' });
      } catch (e) {
        console.error(e);
        if (alive) setCallbackError(t('wf_oauth_restart'));
      } finally {
        if (alive) setPageLoading(false);
      }
    })();
    return () => { alive = false; };
    // Mount-only: the callback is read exactly once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Notifications: the first e-mail slot is the account e-mail, kept in sync until the owner edits it.
  useEffect(() => {
    if (!userEmail || notifyEmailUserEditedRef.current) return;
    setNotificationEmails((prev) => { const next = prev.length ? prev.slice() : ['']; next[0] = userEmail; return next; });
  }, [userEmail]);

  // Plan: stop polling on unmount and when leaving the plan step.
  useEffect(() => () => { if (planPollRef.current) clearInterval(planPollRef.current); }, []);
  useEffect(() => {
    if (step !== STEP_PLAN && planPollRef.current) { clearInterval(planPollRef.current); planPollRef.current = null; setIsCheckingPlan(false); }
  }, [step]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}subscription_plans`);
        const data = await r.json();
        if (alive && Array.isArray(data)) setPlans(data);
      } catch (e) { /* best-effort */ }
      finally { if (alive) setPlansLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const monthlyByName = useMemo(() => { const m = {}; plans.forEach((p) => { if (p.billing_interval !== 'year') m[p.name] = p; }); return m; }, [plans]);
  const yearlyByName = useMemo(() => { const m = {}; plans.forEach((p) => { if (p.billing_interval === 'year') m[p.name] = p; }); return m; }, [plans]);
  const pricingTiers = useMemo(() => WFO_TIER_ORDER.map((n) => monthlyByName[n]).filter(Boolean), [monthlyByName]);
  const pricingSavings = useCallback((name) => {
    const m = monthlyByName[name]; const y = yearlyByName[name];
    const mp = stripeDisplayPrice(m); const yp = stripeDisplayPrice(y);
    if (m && y && mp > 0) return { pct: Math.round(((mp - yp) / mp) * 100), yearlyMonthly: yp, monthly: mp };
    return null;
  }, [monthlyByName, yearlyByName]);

  // ── business context: once, from the real ingested content, after every source finished ──
  const generateBusinessContext = useCallback(async (botId) => {
    if (!botId) return;
    try {
      await authFetch(`${API}onboarding/generate_business_context`, { method: 'POST', body: JSON.stringify({ bot_id: botId }) });
    } catch (e) { console.error('business context generation failed', e); }
  }, []);
  const maybeGenerateBusinessContext = useCallback((botId) => {
    if (bizContextFiredRef.current) return;
    const expected = srcExpectedRef.current;
    if (expected.size === 0) return;
    for (const src of expected) { if (!srcDoneRef.current.has(src)) return; }
    bizContextFiredRef.current = true;
    generateBusinessContext(botId);
  }, [generateBusinessContext]);
  const markSourceDone = useCallback((name, botId) => {
    srcDoneRef.current.add(name);
    maybeGenerateBusinessContext(botId);
  }, [maybeGenerateBusinessContext]);

  // ── one WebSocket: website training + document ingest progress ──
  useEffect(() => {
    if (!uid || !bot) return undefined;
    const raw = process.env.REACT_APP_API_WEBSOCKET_URL;
    let ws;
    try {
      if (!raw) throw new Error('WebSocket URL missing');
      ws = new WebSocket(raw.replace(/^http/, 'ws'));
    } catch (e) { console.error('WS init failed', e); return undefined; }
    ws.addEventListener('open', () => {
      ws.send(JSON.stringify({ type: 'init', user_id: uid, token: token || localStorage.getItem('token') }));
    });
    ws.addEventListener('message', async (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (msg.type === 'find_new_links_result' && msg.request_id === trainingReqRef.current) {
        const links = (msg.new_found_links || []).slice(0, 50); // first run: at most 50 pages
        if (!links.length) {
          setTraining({ active: true, phase: 'none', found: 0, done: 0 });
          setTimeout(() => setTraining((tr) => ({ ...tr, active: false })), 2600);
          markSourceDone('website', bot);
          return;
        }
        setTraining({ active: true, phase: 'training', found: links.length, done: 0 });
        try {
          const r = await authFetch(`${API}scrape_link`, { method: 'POST', body: JSON.stringify({ bot_id: bot, user_id: uid, urls: links }) });
          const j = await r.json();
          (j.knowledge_ids || []).forEach((id) => trainingKnowledgeRef.current.add(id));
          if (!trainingKnowledgeRef.current.size) markSourceDone('website', bot);
        } catch (e) { console.error('scrape_link failed', e); markSourceDone('website', bot); }
        return;
      }
      if (msg.type === 'find_new_links_error' && msg.request_id === trainingReqRef.current) {
        setTraining({ active: true, phase: 'none', found: 0, done: 0 });
        setTimeout(() => setTraining((tr) => ({ ...tr, active: false })), 2600);
        markSourceDone('website', bot);
        return;
      }
      if (msg.knowledge_id != null && trainingKnowledgeRef.current.has(msg.knowledge_id)) {
        if (msg.status === 'completed' || msg.status === 'failed') {
          setTraining((tr) => {
            const done = Math.min(tr.found, tr.done + 1);
            if (done >= tr.found && tr.found > 0) {
              setTimeout(() => setTraining((x) => ({ ...x, active: false })), DONE_HIDE_MS);
              markSourceDone('website', bot);
              return { ...tr, done, phase: 'done' };
            }
            return { ...tr, done };
          });
        }
        return;
      }
      if (msg.knowledge_id != null && docKnowledgeRef.current.has(msg.knowledge_id)) {
        if (msg.status === 'completed' || msg.status === 'failed') {
          setDocSync((d) => {
            const done = Math.min(d.total, d.done + 1);
            if (done >= d.total && d.total > 0) {
              setTimeout(() => setDocSync((x) => ({ ...x, active: false })), DONE_HIDE_MS);
              markSourceDone('docs', bot);
              return { ...d, done, phase: 'done' };
            }
            return { ...d, done };
          });
        }
      }
    });
    return () => ws.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, bot]);

  // ── progress in the DB (resumable steps only, once the agent exists) ──
  useEffect(() => {
    if (step < STEP_DESIGN || !bot || !uid || !token) return;
    authFetch(`${API}onboarding/progress`, { method: 'PUT', body: JSON.stringify({ step, bot_id: bot }) }).catch(() => {});
  }, [step, bot, uid, token]);

  // ── the widget config when entering Design. The background finisher writes it a moment after the agent exists, so
  //    poll; raw fetch, because the global interceptor shows a modal for these expected 404s. ──
  useEffect(() => {
    if (step !== STEP_DESIGN || !bot || widgetFetchedRef.current) return undefined;
    widgetFetchedRef.current = true;
    let cancelled = false;
    (async () => {
      const rawFetch = window._originalFetch || fetch;
      let d = null;
      for (let i = 0; i < 15 && !cancelled; i++) {
        try {
          const res = await rawFetch(`${API}widget_configuration/${encodeURIComponent(bot)}`);
          if (res.ok) { d = await res.json(); break; }
        } catch (e) { /* keep waiting */ }
        await new Promise((r) => setTimeout(r, 1000));
      }
      if (cancelled || !d) return;
      setWidget((w) => ({
        ...w,
        themeColor: d.theme_color?.slice(0, 7) || w.themeColor,
        headerColor: d.header_font_color?.slice(0, 7) || w.headerColor,
        hoverColor: d.button_hover_color || w.hoverColor,
        pulsing: d.pulsing ?? w.pulsing,
        widgetHorizontalAlignment: d.widget_horizontal_alignment || w.widgetHorizontalAlignment,
        widgetBorderRadius: d.widget_border_radius ?? w.widgetBorderRadius,
        widgetSize: d.widget_size ?? w.widgetSize,
        headerText: d.header_text || w.headerText,
        welcomeMessage: Array.isArray(d.welcome_message) ? (d.welcome_message[0] || w.welcomeMessage) : (d.welcome_message || w.welcomeMessage),
        icon: d.widget_icon_path || '',
        logo: d.header_icon_path || '',
        avatar: d.avatar_icon_path || '',
        predefinedQuestions: Array.isArray(d.predefined_questions) ? d.predefined_questions.filter(Boolean).slice(0, 3) : [],
        // what the owner already changed here wins over a late load
        ...Object.fromEntries([...dirtyWidgetRef.current].map((key) => [key, w[key]])),
      }));
    })();
    return () => { cancelled = true; };
  }, [step, bot]);

  useEffect(() => {
    const el = previewBodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [previewMsgs]);
  useEffect(() => () => { if (previewReplyRef.current) clearTimeout(previewReplyRef.current); }, []);
  useEffect(() => {
    if (!previewMenuOpen) return undefined;
    const onDown = (e) => { if (previewMenuRef.current && !previewMenuRef.current.contains(e.target)) setPreviewMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [previewMenuOpen]);

  // cycle the radar's status line while it shows
  useEffect(() => {
    if (!scanning) { setScanStep(0); return undefined; }
    const id = setInterval(() => setScanStep((s) => (s + 1) % 4), 1300);
    return () => clearInterval(id);
  }, [scanning]);

  // ── account ──
  function signedIn(j) {
    setUid(j.user_id); setToken(j.token);
    if (j.token) localStorage.setItem('token', j.token);
    if (j.user_id) localStorage.setItem('user_id', String(j.user_id));
    if (j.first_name) { setFirstName(j.first_name); localStorage.setItem('first_name', j.first_name); }
    applySites(j.urls);
    setStep((s) => Math.max(s, 1));
  }

  function switchAuthMode(mode, { keepEmail = true } = {}) {
    setErr(''); setNotice(''); setPw(''); setPw2(''); setSigninCode(''); setCodeSent(false);
    if (!keepEmail) setUserEmail('');
    setAuthMode(mode);
  }

  async function handleRegister(e) {
    e.preventDefault();
    setErr('');
    const email = (userEmail || '').trim(), first = (firstName || '').trim();
    if (!EMAIL_RE.test(email)) { setErr(t('wxo_err_email')); return; }
    if (!first) { setErr(t('wxo_err_first_name')); return; }
    if (pw.length < 8) { setErr(t('wxo_err_password_short')); return; }
    if (pw !== pw2) { setErr(t('wxo_err_password_match')); return; }
    try {
      setLoading(true);
      // The Webflow token stays in the server-side registration; the account takes the e-mail typed here.
      const r = await fetch(`${API}auth/webflow/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ registration_id: registrationId, email, password: pw, first_name: first }),
      });
      if (r.status === 409) {
        const body = await r.json().catch(() => ({}));
        switchAuthMode('login');
        if (body.email) setUserEmail(body.email);
        setNotice(t('wf_account_exists_code', { email: body.email || email }));
        return;
      }
      if (r.status === 400) {
        const body = await r.json().catch(() => ({}));
        if (/expired/i.test(body.detail || '')) { setCallbackError(t('wf_oauth_restart')); return; }
        if (body.detail === 'invalid_email') { setErr(t('wxo_err_email')); return; }
        throw new Error('register failed');
      }
      if (!r.ok) throw new Error('register failed');
      saveInteraction({ action: 'webflow_account_created' });
      signedIn(await r.json());
    } catch { setErr(t('wxo_err_create_account')); }
    finally { setLoading(false); }
  }

  async function handleLogin(e) {
    e.preventDefault();
    setErr('');
    try {
      setLoading(true);
      // Signing in also attaches this install's parked Webflow authorization to that account.
      const r = await fetch(`${API}auth/webflow/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(codeSent
          ? { registration_id: registrationId, email: userEmail, code: signinCode.trim() }
          : { registration_id: registrationId, email: userEmail, password: pw }),
      });
      if (r.ok) { signedIn(await r.json()); return; }
      if (r.status === 400) { setCallbackError(t('wf_oauth_restart')); return; }
      setErr(codeSent ? t('wf_code_invalid') : t('wxo_err_invalid_login'));
    } catch { setErr(t('wxo_err_generic')); }
    finally { setLoading(false); }
  }

  // No password at hand: a 6-digit code to the account's address signs in, and the install continues right here.
  async function sendCode() {
    setErr(''); setNotice('');
    const email = (userEmail || '').trim();
    if (!EMAIL_RE.test(email)) { setErr(t('wxo_err_email')); return; }
    try {
      setLoading(true);
      const r = await fetch(`${API}auth/webflow/login_code`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ registration_id: registrationId, email }),
      });
      if (r.ok) {
        setCodeSent(true); setSigninCode(''); setPw('');
        setNotice(t('wf_code_sent', { email }));
      } else if (r.status === 404) {
        setErr(t('wf_no_account_for_email', { email }));
      } else if (r.status === 429) {
        setErr(t('wf_too_many_codes'));
      } else if (r.status === 400) {
        const body = await r.json().catch(() => ({}));
        if (/expired/i.test(body.detail || '')) setCallbackError(t('wf_oauth_restart'));
        else setErr(t('wf_code_send_failed'));
      } else {
        setErr(t('wf_code_send_failed'));
      }
    } catch { setErr(t('wf_code_send_failed')); }
    finally { setLoading(false); }
  }

  // Webflow returned no site for this authorization: authorize again and pick one, instead of a dead end.
  async function chooseSiteAgain() {
    try { await startWebflowConnect({ authorize: true }); }
    catch (e) { console.error('Webflow re-authorization failed:', e); setErr(t('wf_oauth_restart')); }
  }

  // ── knowledge ──
  function pickSite(key) {
    const next = sites.find((s) => `${s.site_id}|${s.url}` === key);
    setSiteKey(key);
    // follow the picked site unless the owner typed another address for the knowledge
    if (next) setUrl((u) => (!u || sites.some((s) => siteHref(s.url) === u) ? siteHref(next.url) : u));
  }

  async function startWebsiteTraining(botId, trainUrl) {
    if (trainedBotsRef.current.has(botId)) return; // one website run per agent
    trainedBotsRef.current.add(botId);
    srcExpectedRef.current.add('website');
    setTraining({ active: true, phase: 'searching', found: 0, done: 0 });
    trainingKnowledgeRef.current = new Set();
    const reqId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    trainingReqRef.current = reqId;
    try {
      await authFetch(`${API}find_new_links`, { method: 'POST', body: JSON.stringify({ bot_id: botId, url: trainUrl, request_id: reqId }) });
    } catch (e) { console.error('find_new_links failed', e); setTraining((tr) => ({ ...tr, active: false })); markSourceDone('website', botId); }
  }

  async function startDocumentIngest(botId, urls) {
    srcExpectedRef.current.add('docs');
    setDocSync({ active: true, phase: 'processing', done: 0, total: urls.length });
    docKnowledgeRef.current = new Set();
    try {
      const r = await authFetch(`${API}ingest_document_urls`, { method: 'POST', body: JSON.stringify({ bot_id: botId, user_id: uid, document_urls: urls }) });
      const j = await r.json();
      (j.knowledge_ids || []).forEach((id) => docKnowledgeRef.current.add(id));
      if (!docKnowledgeRef.current.size) { setDocSync({ active: false, phase: 'idle', done: 0, total: 0 }); markSourceDone('docs', botId); }
    } catch (e) {
      console.error('ingest_document_urls failed', e);
      setDocSync({ active: false, phase: 'idle', done: 0, total: 0 });
      markSourceDone('docs', botId);
    }
  }

  // The upload modal hands over the Azure URLs first and their metadata ([{ name, url, size }]) second, as on Wix;
  // without metadata the names come from the URLs.
  function handleDocsAdded(urls, files) {
    const items = (files && files.length)
      ? files
      : (urls || []).filter((u) => typeof u === 'string' && u)
        .map((u) => ({ url: u, name: decodeURIComponent((u.split('?')[0].split('/').pop()) || 'document') }));
    setDocFiles((prev) => [...prev, ...items.filter((f) => f && f.url)]);
    setDocModalOpen(false);
  }
  function removeDoc(i) { setDocFiles((prev) => prev.filter((_, idx) => idx !== i)); }

  // Icon, colours, welcome and two questions from the website, polled until ready (at most 45 s, never blocking).
  async function prepareBrand(botId) {
    const until = Date.now() + 45000;
    while (Date.now() < until) {
      const r = await authFetch(`${API}onboarding/prepare_brand?bot_id=${encodeURIComponent(botId)}`, { method: 'POST' });
      if (!r?.ok) return;
      const v = await r.json();
      if (v.status !== 'pending') return;
      await new Promise((res) => setTimeout(res, 800));
    }
  }

  // Build: the agent at once (bound to the chosen Webflow site), the radar while its look is prepared from the
  // website, then Design; website and documents keep learning in the background with a docked progress strip.
  async function createBotAndContinue() {
    setErr('');
    const trainUrl = url.trim();
    if (!siteId) { setErr(t('wf_no_sites_text')); return; }
    if (trainUrl && !isValidUrlSimple(trainUrl)) { setErr(t('wfo_err_url')); return; }
    if (bot) { setStep(STEP_DESIGN); return; } // back from Design: the agent exists already
    try {
      setLoading(true);
      const r = await authFetch(`${API}webflow/onboarding/create_bot`, {
        method: 'POST',
        body: JSON.stringify({
          bot_name: (site?.name || '').trim() || domainName(trainUrl || site?.url),
          url: trainUrl ? siteHref(trainUrl) : null,
          webflow_site_id: siteId,
        }),
      });
      const j = await r.json();
      if (!j.bot_id) throw new Error(j.error || 'no bot id');
      selectAgentInPortal(j.bot_id);
      const botId = j.bot_id;
      bizContextFiredRef.current = false;
      srcExpectedRef.current = new Set();
      srcDoneRef.current = new Set();
      widgetFetchedRef.current = false;
      setBot(botId);
      if (window.dataLayer) window.dataLayer.push({ event: 'bot_creation_started' });
      saveInteraction({ action: 'webflow_bot_creation_started' });
      if (trainUrl) startWebsiteTraining(botId, siteHref(trainUrl));
      if (docFiles.length) startDocumentIngest(botId, docFiles.map((f) => f.url));
      setLoading(false);
      setScanning(true);
      await Promise.all([
        prepareBrand(botId).catch(() => {}),
        new Promise((res) => setTimeout(res, SCAN_MIN_MS)),
      ]);
      setScanning(false);
      setStep(STEP_DESIGN);
    } catch (e) {
      console.error(e);
      setScanning(false);
      setErr(t('wxo_err_build'));
    } finally { setLoading(false); }
  }

  // ── design ──
  const setStyle = (k, v) => {
    dirtyWidgetRef.current.add(k);
    setWidget((w) => ({ ...w, [k]: v }));
  };
  function handleImageUpload(e, key) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setStyle(key, ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  }
  async function uploadImage(base64) {
    const fd = new FormData();
    fd.append('file', base64ToFile(base64, `icon-${Date.now()}.png`));
    const res = await fetch(`${API}upload_image/`, { method: 'POST', body: fd });
    if (!res.ok) throw new Error('upload failed');
    const j = await res.json();
    return j.file_path;
  }
  async function saveDesignAndContinue() {
    setErr('');
    try {
      setLoading(true);
      let iconPath = widget.icon;
      if (iconPath && iconPath.startsWith('data:image/')) iconPath = await uploadImage(iconPath);
      let logoPath = widget.logo;
      if (logoPath && logoPath.startsWith('data:image/')) logoPath = await uploadImage(logoPath);
      let avatarPath = widget.avatar;
      if (avatarPath && avatarPath.startsWith('data:image/')) avatarPath = await uploadImage(avatarPath);
      const updates = {
        theme_color: widget.themeColor,
        header_font_color: widget.headerColor,
        button_hover_color: widget.hoverColor,
        pulsing: widget.pulsing,
        widget_horizontal_alignment: widget.widgetHorizontalAlignment,
        widget_size: widget.widgetSize,
        widget_border_radius: widget.widgetBorderRadius,
        header_text: widget.headerText,
        welcome_message: [widget.welcomeMessage],
        predefined_questions: widget.predefinedQuestions.map((q) => q.trim()).filter(Boolean).slice(0, 3),
        widget_icon_path: iconPath || '',
        header_icon_path: logoPath || '',
        avatar_icon_path: avatarPath || '',
      };
      const res = await fetch(`${API}widget_configuration/${encodeURIComponent(bot)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error('save failed');
      if (iconPath !== widget.icon) setStyle('icon', iconPath || '');
      if (logoPath !== widget.logo) setStyle('logo', logoPath || '');
      if (avatarPath !== widget.avatar) setStyle('avatar', avatarPath || '');
      setStep(STEP_TEST);
    } catch (e) { console.error(e); setErr(t('wxo_design_save_error')); }
    finally { setLoading(false); }
  }

  const imageUploadControl = (key, labelKey, ref) => (
    <div className="wxo-control-group">
      <div className="wxo-color-picker-container">
        <label className="wxo-widget-setting-title">{t(labelKey)}</label>
        <div className="wxo-logo-upload">
          {widget[key] && <img className="wxo-logo-preview" src={widget[key]} alt="" />}
          <button type="button" className="wxo-logo-upload-btn" onClick={() => ref.current && ref.current.click()}>
            <FontAwesomeIcon icon={faUpload} /><span>{widget[key] ? t('wxo_design_replace') : t('wxo_design_upload')}</span>
          </button>
          {widget[key] && <button type="button" className="wxo-logo-remove-btn" onClick={() => setStyle(key, '')} aria-label="remove"><FontAwesomeIcon icon={faXmark} /></button>}
          <input ref={ref} type="file" accept="image/*" onChange={(e) => handleImageUpload(e, key)} style={{ display: 'none' }} />
        </div>
      </div>
    </div>
  );

  function sendPreview() {
    const text = previewInput.trim();
    if (!text) return;
    setPreviewMsgs((m) => [...m, { role: 'user', text }]);
    setPreviewInput('');
    if (previewReplyRef.current) clearTimeout(previewReplyRef.current);
    previewReplyRef.current = setTimeout(() => {
      setPreviewMsgs((m) => [...m, { role: 'bot', text: t('wxo_design_preview_reply') }]);
    }, 500);
  }
  function clearPreviewChat() {
    if (previewReplyRef.current) clearTimeout(previewReplyRef.current);
    setPreviewMsgs([]); setPreviewInput(''); setPreviewMenuOpen(false);
  }

  // ── add to site: ONE explicit click, "Publish AI Agent". It is never triggered on its own (Webflow
  //    #1179689/#1197336: the customer decides when to publish). Registers + applies the script, publishes the
  //    site, moves on.
  //    If the publish fails after the script is on, the next click only retries the publish. ──
  async function addAndPublish() {
    if (addBusyRef.current || !bot || !siteId) return;
    addBusyRef.current = true;
    setAddError('');
    const q = `bot_id=${encodeURIComponent(bot)}&site_id=${encodeURIComponent(siteId)}`;
    const failure = async (res, fallback) => {
      const body = await res.json().catch(() => ({}));
      if (res.status === 429) return t('integration_webflow_rate_limit');
      if (body.status === 'token_revoked') return t('wf_token_revoked');
      return fallback;
    };
    try {
      if (!activatedRef.current) {
        setAddPhase('adding');
        const res = await authFetch(`${API}webflow/activate_chatbot?${q}`, { method: 'POST' });
        if (!res.ok) { setAddError(await failure(res, t('wf_add_to_site_failed'))); return; }
        activatedRef.current = true;
        saveInteraction({ action: 'webflow_widget_activated' });
      }
      setAddPhase('publishing');
      const res = await authFetch(`${API}webflow/publish_site?${q}`, { method: 'POST' });
      if (!res.ok) { setAddError(await failure(res, t('wf_publish_failed'))); return; }
      saveInteraction({ action: 'webflow_site_published' });
      setLiveOnSite(true);
      setStep(STEP_NOTIFY);
    } catch (e) {
      console.error('Webflow add and publish failed:', e);
      setAddError(activatedRef.current ? t('wf_publish_failed') : t('wf_add_to_site_failed'));
    } finally {
      addBusyRef.current = false;
      setAddPhase('');
    }
  }

  // ── notifications ──
  const notifyEmailRequired = WFO_NOTIFY_EVENTS.some((ev) => !wfoChannelLocked(ev.key, 'email') && Boolean(notifyPrefs[ev.key]?.email));
  const notifyCleanEmails = (notificationEmails || []).map((e) => String(e).trim()).filter((e) => e.length);
  const notifyInvalidEmails = notifyCleanEmails.filter((e) => !EMAIL_RE.test(e));
  const notifyEmailMissing = notifyEmailRequired && notifyCleanEmails.length === 0;
  const notifyEmailHasError = notifyEmailMissing || notifyInvalidEmails.length > 0;

  async function saveNotificationsAndContinue() {
    setNotifySaveAttempted(true);
    if (notifyEmailHasError) return;
    setErr(''); setLoading(true);
    try {
      const valid = notifyCleanEmails.filter((e) => EMAIL_RE.test(e));
      const needs = (sec) => WFO_NOTIFY_EVENTS.some((ev) => ev.section === sec && !wfoChannelLocked(ev.key, 'email') && Boolean(notifyPrefs[ev.key]?.email));
      const payload = {
        prefs: notifyPrefs,
        emails_by_section: { live_chat: needs('live_chat') ? valid : [], leads: needs('leads') ? valid : [] },
        slack_webhooks_by_section: { live_chat: [], leads: [] },
        teams_webhooks_by_section: { live_chat: [], leads: [] },
        telegram_chat_ids_by_section: { live_chat: [], leads: [] },
      };
      if (bot) {
        try { await authFetch(`${API}notification_settings/${encodeURIComponent(bot)}`, { method: 'PUT', body: JSON.stringify(payload) }); }
        catch (e) { console.error('notification_settings PUT failed', e); }
      }
    } finally { setLoading(false); setStep(STEP_PLAN); }
  }

  // ── plan: Stripe checkout in a NEW TAB, this tab polls the agent's plan until it is no longer "Free" ──
  function stopPlanPolling() {
    if (planPollRef.current) { clearInterval(planPollRef.current); planPollRef.current = null; }
    setIsCheckingPlan(false);
  }
  async function fetchBotPlanName() {
    if (!bot) throw new Error('no bot');
    const r = await fetch(`${API}bot?bot_id=${encodeURIComponent(bot)}&include_plan=true`);
    if (!r.ok) throw new Error('bot fetch failed');
    const j = await r.json();
    return j?.subscription_plan?.name ?? 'Free';
  }
  function startPlanPolling() {
    if (planPollRef.current || !bot) return;
    setPlanCheckError('');
    setIsCheckingPlan(true);
    planPollRef.current = setInterval(async () => {
      try {
        const name = await fetchBotPlanName();
        if (name && name !== 'Free') {
          if (window.dataLayer) window.dataLayer.push({ event: 'free_trial_started' });
          saveInteraction({ action: 'webflow_payment_confirmed' });
          stopPlanPolling();
          setTrialStarted(true);
        }
      } catch (e) { setPlanCheckError(t('wxo_price_still_checking')); }
    }, 2000);
  }
  async function startTrialCheckout(planName) {
    const row = billingCycle === 'YEAR' ? (yearlyByName[planName] || monthlyByName[planName]) : monthlyByName[planName];
    if (!bot || !row?.stripe_subscription_plan_id) {
      console.error('pricing: missing agent or Stripe price id');
      setErr(t('billing_checkout_start_error'));
      return;
    }
    setErr('');
    if (window.dataLayer) window.dataLayer.push({ event: 'checkout_session_started' });
    selectAgentInPortal(bot);
    // Opened synchronously from the click (popup blockers), the checkout address follows.
    const tab = window.open('', '_blank');
    try {
      const res = await fetch(`${API}create-checkout-session`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bot_id: bot, price_id: row.stripe_subscription_plan_id, subscription_plan_id: row.id, coupon_id: row.coupon_id }),
      });
      const data = await res.json();
      if (!data?.checkout_url) throw new Error('no checkout url');
      saveInteraction({ action: 'webflow_checkout_session_started' });
      if (tab) { tab.opener = null; tab.location.href = data.checkout_url; }
      else window.open(data.checkout_url, '_blank', 'noopener,noreferrer');
      startPlanPolling();
    } catch (e) {
      if (tab) tab.close();
      console.error('checkout failed:', e);
      setErr(t('billing_checkout_start_error'));
    }
  }

  async function goToPortal() {
    try { if (bot) await authFetch(`${API}onboarding/complete`, { method: 'POST', body: JSON.stringify({ bot_id: bot }) }); } catch (e) { /* best-effort */ }
    // The install is finished: a later visit to this route starts a fresh authorization.
    callbackExchange = null;
    selectAgentInPortal(bot);
    if (onSetupComplete) onSetupComplete(true, token, uid); // signs the app shell in and opens the overview
    else navigate('/');
  }

  // ── progress strips ──
  const doneTimer = () => (
    <span className="wxo-strip-timer" aria-hidden="true">
      <svg viewBox="0 0 24 24" className="wxo-strip-timer-svg">
        <circle className="wxo-strip-timer-track" cx="12" cy="12" r="10" />
        <circle className="wxo-strip-timer-ring" cx="12" cy="12" r="10" style={{ animationDuration: `${DONE_HIDE_MS}ms` }} />
      </svg>
      <FontAwesomeIcon icon={faCheck} className="wxo-strip-timer-check" />
    </span>
  );
  const trainPct = Math.round((training.done / Math.max(1, training.found)) * 100);
  const trainStrip = training.active ? (
    <div className="wxo-train-strip">
      <div className="wxo-train-inner">
        {training.phase === 'searching' && (<><span className="wxo-train-spin" /><span className="wxo-train-label">{t('wxo_train_searching')}</span></>)}
        {training.phase === 'training' && (
          <div className="wxo-train-prog">
            <div className="wxo-train-row">
              <span className="wxo-train-label">{t('wxo_train_progress', { done: training.done, total: training.found })}</span>
              <span className="wxo-train-pct">{trainPct}%</span>
            </div>
            <div className="wxo-train-bar"><div className="wxo-train-fill" style={{ width: `${trainPct}%` }} /></div>
          </div>
        )}
        {training.phase === 'done' && (<>{doneTimer()}<span className="wxo-train-label">{t('wxo_train_done', { count: training.found })}</span></>)}
        {training.phase === 'none' && (<span className="wxo-train-label">{t('wxo_train_none')}</span>)}
      </div>
    </div>
  ) : null;
  const docPct = Math.round((docSync.done / Math.max(1, docSync.total)) * 100);
  const docStrip = docSync.active ? (
    <div className="wxo-train-strip">
      <div className="wxo-train-inner">
        {docSync.phase === 'processing' ? (
          <div className="wxo-train-prog">
            <div className="wxo-train-row">
              <span className="wxo-train-label">{t('wxo_docs_processing', { done: docSync.done, total: docSync.total })}</span>
              <span className="wxo-train-pct">{docPct}%</span>
            </div>
            <div className="wxo-train-bar"><div className="wxo-train-fill" style={{ width: `${docPct}%` }} /></div>
          </div>
        ) : (<>{doneTimer()}<span className="wxo-train-label">{t('wxo_docs_done', { count: docSync.total })}</span></>)}
      </div>
    </div>
  ) : null;
  const progressStrips = (training.active || docSync.active) ? (<>{trainStrip}{docStrip}</>) : null;

  // ── render helpers ──
  // No way back from Design into the build (the agent exists); Back from Design shows the knowledge step read-only.
  const backBtn = step > 0 ? (
    <button className="wxo-back-inline" type="button" aria-label={t('wxo_back')} onClick={() => setStep((s) => Math.max(1, s - 1))}>
      <FontAwesomeIcon icon={faArrowLeft} /><span>{t('wxo_back')}</span>
    </button>
  ) : null;
  const titleRow = (heading) => <div className="wxo-title-row">{step > 1 ? backBtn : null}{heading}</div>;

  const langToggle = (
    <div className="wxo-lang" ref={langRef}>
      <button type="button" className="wxo-lang-trigger" onClick={() => setLangOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={langOpen}>
        <FontAwesomeIcon icon={faGlobe} className="wxo-lang-icon" />
        <span className="wxo-lang-current">{currentLangLabel}</span>
      </button>
      {langOpen && (
        <div className="wxo-lang-menu" role="listbox">
          {SUPPORTED_LANGUAGES.map((l) => {
            const sel = l.code === currentLanguage;
            return (
              <button key={l.code} type="button" role="option" aria-selected={sel} className={`wxo-lang-option ${sel ? 'wxo-sel' : ''}`}
                onClick={() => { handleLanguageChange(l.code); setLangOpen(false); }}>
                <span className="wxo-lang-check">{sel && <FontAwesomeIcon icon={faCheck} />}</span>{l.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  const shell = (children, dotsStep = step) => (
    <div className="wxo-scope wxo-wrapper wfo-wrapper">
      <img className="wxo-logo" src="/ultimo-bots-white.png" alt="Ultimo Bots" />
      {langToggle}
      {dotsStep !== null && (
        <div className="wxo-step-ind">
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <span key={i} className={`wxo-step-dot ${i === dotsStep ? 'wxo-current' : i < dotsStep ? 'wxo-done' : ''}`} />
          ))}
        </div>
      )}
      {children}
    </div>
  );

  if (pageLoading) {
    return shell(<div className="wxo-stage" style={{ maxWidth: 480 }}><div className="wxo-trial-wait"><div className="wxo-scan-ring" /></div></div>, null);
  }

  if (callbackError) {
    return shell(
      <div className="wxo-stage wxo-has-cta" style={{ maxWidth: 540 }} data-testid="wf-callback-error">
        <div className="wxo-card-anim">
          <div className="wxo-card">
            <h2 className="wxo-h2">{t('wf_oauth_error_title')}</h2>
            <p className="wxo-sub">{callbackError}</p>
            {localStorage.getItem('token') && (
              <p className="wxo-link">
                <button type="button" onClick={() => { callbackExchange = null; if (onConnected) onConnected('/overview'); else navigate('/overview', { replace: true }); }}>
                  {t('wf_back_to_dashboard')}
                </button>
              </p>
            )}
          </div>
          <div className="wxo-stage-cta">
            <button type="button" className="wxo-cta-below" onClick={() => { callbackExchange = null; window.location.assign(`${API}auth/webflow/connect`); }}>
              <span>{t('wf_oauth_try_again')}</span><FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
        </div>
      </div>,
      null,
    );
  }

  // One card at a time; its width snaps per step (the fade-in masks it), the anim key replays the entrance.
  let stageW = 480;
  if (step === 0) stageW = 980;
  else if (step === 1) stageW = scanning ? 640 : 560;
  else if (step === STEP_DESIGN || step === STEP_TEST) stageW = 920;
  else if (step === STEP_ADD) stageW = 600;
  else if (step === STEP_NOTIFY) stageW = 760;
  else if (step === STEP_PLAN) stageW = 1000;
  const animKey = step === 1 ? `k-${scanning ? 'scan' : 'input'}` : `s-${step}`;

  // The primary CTA renders BELOW the card on the purple shell; Account and Plan keep their in-card buttons.
  let stageCta = null;
  if (step === 1 && !scanning) stageCta = { label: bot ? t('wxo_continue') : t('wxo_build_agent'), onClick: createBotAndContinue, busy: loading, disabled: !siteId };
  else if (step === STEP_DESIGN) stageCta = { label: t('wxo_to_test'), onClick: saveDesignAndContinue, busy: loading };
  else if (step === STEP_TEST) stageCta = { label: t('wxo_test_continue'), onClick: () => setStep(STEP_ADD), busy: false };
  else if (step === STEP_ADD) stageCta = liveOnSite
    ? { label: t('wxo_continue'), onClick: () => setStep(STEP_NOTIFY), busy: false }
    : { label: t('wfo_add_publish_button'), onClick: addAndPublish, busy: Boolean(addPhase), disabled: !bot || !siteId };
  else if (step === STEP_NOTIFY) stageCta = { label: t('wxo_notif_continue'), onClick: saveNotificationsAndContinue, busy: loading };

  return shell(
    <>
      <div className={`wxo-stage ${stageCta ? 'wxo-has-cta' : ''}`} style={{ maxWidth: stageW }}>
        <div className="wxo-card-anim" key={animKey}>

          {/* ── 0 — Account ── */}
          {step === 0 && (
            <div className="wxo-card wxo-card-auth">
              <div className="wxo-auth-form">
                {authMode === 'register' ? (
                  <>
                    <h2 className="wxo-h2">{t('wxo_reg_title')}</h2>
                    <p className="wxo-sub">{t('wfo_reg_sub')}</p>
                    <form onSubmit={handleRegister} autoComplete="on">
                      <div className="wxo-field"><TextField label={t('wxo_first_name')} name="firstName" autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} fullWidth variant="outlined" required sx={FIELD_SX} /></div>
                      <div className="wxo-field"><TextField label={t('wxo_email')} name="email" type="email" autoComplete="email" value={userEmail} onChange={(e) => setUserEmail(e.target.value)} fullWidth variant="outlined" required sx={FIELD_SX} /></div>
                      <div className="wxo-field"><TextField label={t('wxo_password')} name="new-password" type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} fullWidth variant="outlined" required inputProps={{ minLength: 8 }} sx={FIELD_SX} /></div>
                      <div className="wxo-field"><TextField label={t('wxo_confirm_password')} name="confirm-password" type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} fullWidth variant="outlined" required inputProps={{ minLength: 8 }} sx={FIELD_SX} /></div>
                      {notice && !err && <div className="wfo-notice" role="status">{notice}</div>}
                      {err && <div className="wxo-err">{err}</div>}
                      <button className="wxo-btn wxo-btn-wide" type="submit" disabled={loading}>{t('wxo_get_started')} {loading ? <span className="wxo-spin" /> : <FontAwesomeIcon icon={faArrowRight} />}</button>
                    </form>
                    <p className="wxo-link">{t('wxo_have_account')}{' '}<button type="button" onClick={() => switchAuthMode('login')}>{t('wxo_log_in')}</button></p>
                  </>
                ) : (
                  <>
                    <h2 className="wxo-h2">{t('wxo_login_title')}</h2>
                    <p className="wxo-sub">{t('wfo_login_sub')}</p>
                    <form onSubmit={handleLogin} autoComplete="on">
                      <div className="wxo-field"><TextField label={t('wxo_email')} name="email" type="email" autoComplete="email" value={userEmail} onChange={(e) => setUserEmail(e.target.value)} fullWidth variant="outlined" required sx={FIELD_SX} /></div>
                      {codeSent ? (
                        <div className="wxo-field"><TextField label={t('wf_code_placeholder')} name="one-time-code" autoComplete="one-time-code" value={signinCode}
                          onChange={(e) => setSigninCode(e.target.value.replace(/\D/g, '').slice(0, 6))} fullWidth variant="outlined" required
                          inputProps={{ inputMode: 'numeric', maxLength: 6 }} sx={FIELD_SX} /></div>
                      ) : (
                        <div className="wxo-field"><TextField label={t('wxo_password')} name="password" type="password" autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)} fullWidth variant="outlined" required sx={FIELD_SX} /></div>
                      )}
                      {/* One "Forgot your password?" (right, as on Wix): it e-mails the sign-in code. With a code sent:
                          a new code, or back to the password. */}
                      {codeSent ? (
                        <div className="wfo-code-row">
                          <button type="button" className="wfo-text-btn" onClick={sendCode} disabled={loading}>{t('wf_resend_code')}</button>
                          <button type="button" className="wfo-text-btn" onClick={() => { setCodeSent(false); setSigninCode(''); setNotice(''); setErr(''); }}>{t('wf_use_password')}</button>
                        </div>
                      ) : (
                        <div className="wxo-forgot-row">
                          <button type="button" className="wfo-text-btn" onClick={sendCode} disabled={loading}>{t('wfo_forgot_password')}</button>
                        </div>
                      )}
                      {notice && !err && <div className="wfo-notice" role="status">{notice}</div>}
                      {err && <div className="wxo-err">{err}</div>}
                      <button className="wxo-btn wxo-btn-wide" type="submit" disabled={loading || (codeSent ? signinCode.trim().length !== 6 : !pw)}>
                        {codeSent ? t('wf_sign_in_with_code') : t('wxo_log_in')} {loading ? <span className="wxo-spin" /> : <FontAwesomeIcon icon={faArrowRight} />}
                      </button>
                    </form>
                    <p className="wxo-link">{t('wxo_new_here')}{' '}<button type="button" onClick={() => switchAuthMode('register')}>{t('wxo_create_account')}</button></p>
                  </>
                )}
              </div>
              <div className="wxo-auth-visual">
                <WixAuthVisual actions={GENERIC_ACTIONS} tools={WFO_TOOLS} eyebrowKey="wfo_wav_eyebrow" titleKey="wav_title" subKey="wfo_wav_sub" />
              </div>
            </div>
          )}

          {/* ── 1 — Knowledge, then the radar while the agent is built ── */}
          {step === 1 && !scanning && (
            <div className="wxo-card">
              {titleRow(<h2 className="wxo-h2">{t('wxo_connect_title')}</h2>)}
              <p className="wxo-sub">{t('wfo_connect_sub')}</p>
              {sites.length === 0 ? (
                <div className="wfo-no-sites">
                  <strong>{t('wf_no_sites_title')}</strong>
                  <span>{t('wf_no_sites_text')}</span>
                  <button type="button" className="wxo-btn" onClick={chooseSiteAgain}>{t('wf_no_sites_button')}</button>
                </div>
              ) : (
                <>
                  {sites.length === 1 || bot ? (
                    <div className="wxo-site-banner">
                      <span className="wxo-site-dot" />
                      <span className="wxo-site-banner-text">{t('wfo_site_connected_to')} <strong>{siteName}</strong></span>
                    </div>
                  ) : (
                    <div className="wfo-site-picker">
                      <TextField select label={t('wfo_site_pick')} value={siteKey} onChange={(e) => pickSite(e.target.value)}
                        fullWidth variant="outlined" size="small" sx={FIELD_SX}>
                        {sites.map((s) => (
                          <MenuItem key={`${s.site_id}|${s.url}`} value={`${s.site_id}|${s.url}`}>
                            <span className="wfo-site-option">
                              <img src="/webflow-logo.png" alt="" />
                              <span className="wfo-site-option-name">{s.name || siteHost(s.url)}</span>
                              <span className="wfo-site-option-host">{siteHost(s.url)}</span>
                            </span>
                          </MenuItem>
                        ))}
                      </TextField>
                    </div>
                  )}
                  <div className="wxo-sources">
                    <div className="wxo-source">
                      <div className="wxo-source-top">
                        <span className="wxo-source-ic"><FontAwesomeIcon icon={faGlobe} /></span>
                        <span className="wxo-source-label">{t('wxo_src_website')}</span>
                      </div>
                      <TextField label={t('wxo_url_placeholder')} type="text" value={url} onChange={(e) => setUrl(e.target.value)}
                        fullWidth variant="outlined" size="small" disabled={!!bot} sx={FIELD_SX} />
                    </div>
                    <div className={`wxo-source wxo-source-docs ${docFiles.length ? 'wxo-has' : ''}`} role="button" tabIndex={0}
                      onClick={() => !bot && setDocModalOpen(true)}
                      onKeyDown={(e) => { if (!bot && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setDocModalOpen(true); } }}>
                      <div className="wxo-source-top">
                        <span className="wxo-source-ic"><FontAwesomeIcon icon={faFileArrowUp} /></span>
                        <span className="wxo-source-label">{t('wxo_src_documents')}</span>
                        {docFiles.length > 0 && !bot
                          ? <button type="button" className="wxo-source-clear" onClick={(e) => { e.stopPropagation(); setDocFiles([]); }}>{t('wxo_docs_clear')}</button>
                          : <FontAwesomeIcon icon={faArrowRight} className="wxo-source-arrow" />}
                      </div>
                      {docFiles.length > 0 ? (
                        <>
                          <ul className="wxo-doc-list">
                            {docFiles.map((f, i) => (
                              <li key={`${f.url}-${i}`} className="wxo-doc-item">
                                <FontAwesomeIcon icon={faFileLines} className="wxo-doc-ic" />
                                <span className="wxo-doc-name" title={f.name}>{f.name}</span>
                                {!bot && <button type="button" className="wxo-doc-rm" onClick={(e) => { e.stopPropagation(); removeDoc(i); }} aria-label={t('wxo_docs_remove')}><FontAwesomeIcon icon={faXmark} /></button>}
                              </li>
                            ))}
                          </ul>
                          {!bot && <span className="wxo-doc-more"><FontAwesomeIcon icon={faPlus} /> {t('wxo_docs_more')}</span>}
                        </>
                      ) : (<span className="wxo-source-hint">{t('wxo_docs_hint')}</span>)}
                    </div>
                  </div>
                </>
              )}
              {err && <div className="wxo-err">{err}</div>}
              <p className="wxo-privacy">{t('wxo_privacy')}</p>
            </div>
          )}

          {step === 1 && scanning && (
            <div className="wxo-card wxo-card-skills">
              <div className="wxo-skills-stage">
                <div className="wxo-skills-grid-bg" aria-hidden="true" />
                <div className="wxo-skills-glow" aria-hidden="true" />
                <div className="wxo-scan2">
                  <div className="wxo-radar" aria-hidden="true">
                    <span className="wxo-radar-ring" />
                    <span className="wxo-radar-ring wxo-radar-ring-2" />
                    <span className="wxo-radar-ring wxo-radar-ring-3" />
                    <span className="wxo-radar-sweep" />
                    <span className="wxo-radar-core wfo-radar-core"><img src="/webflow-logo.png" alt="" /></span>
                  </div>
                  <h2 className="wxo-skills-h2">{t('wfo_scan_title')}</h2>
                  <div className="wxo-scan-status">
                    <span className="wxo-scan-pulse" />
                    <span className="wxo-scan-status-text" key={scanStep}>{t(`wfo_scan_step_${scanStep + 1}`)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── 2 — Design ── */}
          {step === STEP_DESIGN && (
            <div className="wxo-card">
              {titleRow(<h2 className="wxo-h2">{t('wxo_design_title')}</h2>)}
              <p className="wxo-sub">{t('wxo_design_sub')}</p>
              <div className="wxo-preview-card">
                <div className="wxo-user-settings">
                  <div className="wxo-control-group">
                    <TextField label={t('wxo_design_header')} value={widget.headerText} onChange={(e) => setStyle('headerText', e.target.value)}
                      fullWidth variant="outlined" inputProps={{ maxLength: 60 }} helperText={`${(widget.headerText || '').length}/60`} sx={FIELD_SX} />
                  </div>
                  <div className="wxo-control-group">
                    <TextField label={t('wxo_design_welcome')} value={widget.welcomeMessage} onChange={(e) => setStyle('welcomeMessage', e.target.value)}
                      fullWidth multiline minRows={2} maxRows={4} variant="outlined" inputProps={{ maxLength: 300 }}
                      helperText={`${(widget.welcomeMessage || '').length}/300`} sx={FIELD_SX} />
                  </div>
                  <div className="wxo-control-group">
                    <div className="wxo-color-picker-container">
                      <label className="wxo-widget-setting-title">{t('wxo_design_theme')}</label>
                      <input type="color" value={widget.themeColor} onChange={(e) => setStyle('themeColor', e.target.value)} />
                    </div>
                  </div>
                  <div className="wxo-control-group">
                    <div className="wxo-color-picker-container">
                      <label className="wxo-widget-setting-title">{t('wxo_design_header_color')}</label>
                      <input type="color" value={widget.headerColor} onChange={(e) => setStyle('headerColor', e.target.value)} />
                    </div>
                  </div>
                  {imageUploadControl('icon', 'wxo_design_icon', iconInputRef)}
                  {imageUploadControl('logo', 'wxo_design_logo', logoInputRef)}
                  {imageUploadControl('avatar', 'wxo_design_avatar', avatarInputRef)}
                </div>

                <div className="wxo-divider" />

                <div className="wxo-preview-area">
                  <div className="wxo-chat-bubble" style={{ background: widget.icon ? 'transparent' : widget.themeColor }}>
                    {widget.icon ? <img src={widget.icon} alt="" /> : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="58%" viewBox="0 0 24 24" fill="#fff"><path fillRule="evenodd" d="M12 2.25c-2.429 0-4.817.178-7.152.521C2.87 3.061 1.5 4.795 1.5 6.741v6.018c0 1.946 1.37 3.68 3.348 3.97.877.129 1.761.234 2.652.316V21a.75.75 0 0 0 1.28.53l4.184-4.183a.39.39 0 0 1 .266-.112c2.006-.05 3.982-.22 5.922-.506 1.978-.29 3.348-2.023 3.348-3.97V6.741c0-1.947-1.37-3.68-3.348-3.97A49.145 49.145 0 0 0 12 2.25ZM8.25 8.625a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Zm2.625 1.125a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875-1.125a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Z" clipRule="evenodd" /></svg>
                    )}
                  </div>
                  <div className="wxo-chat-window">
                    <div className="wxo-chat-header" style={{ backgroundColor: widget.themeColor }}>
                      <div className="wxo-chat-header-title">
                        <span className="wxo-chat-header-brand">
                          {widget.logo && <img className="wxo-chat-logo" src={widget.logo} alt="" />}
                          <span className="wxo-chat-title" style={{ color: widget.headerColor }}>{widget.headerText || 'Chat with us'}</span>
                        </span>
                        <span className="wxo-chat-actions" ref={previewMenuRef}>
                          <button type="button" className="wxo-chat-kebab" style={{ color: widget.headerColor }}
                            aria-label={t('wxo_design_menu_more')} aria-haspopup="menu" aria-expanded={previewMenuOpen}
                            onClick={() => setPreviewMenuOpen((o) => !o)}>
                            <FontAwesomeIcon icon={faEllipsis} />
                          </button>
                          <button type="button" className="wxo-chat-close" style={{ color: widget.headerColor }} aria-label="close"><FontAwesomeIcon icon={faXmark} /></button>
                          {previewMenuOpen && (
                            <div className="wxo-chat-menu" role="menu">
                              <button type="button" className="wxo-chat-menu-item" role="menuitem" onClick={clearPreviewChat}>
                                <FontAwesomeIcon icon={faTrash} /> {t('wxo_design_menu_clear')}
                              </button>
                              <button type="button" className="wxo-chat-menu-item" role="menuitem" onClick={() => { setPreviewMenuOpen(false); setLiveModalOpen(true); }}>
                                <FontAwesomeIcon icon={faHeadset} /> {t('wxo_design_menu_human')}
                              </button>
                            </div>
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="wxo-chat-body" ref={previewBodyRef}>
                      <div className="wxo-chat-row bot">
                        {widget.avatar && <img className="wxo-chat-avatar" src={widget.avatar} alt="" />}
                        <div className="wxo-chat-msg">{widget.welcomeMessage || 'Hi, how can I assist you?'}</div>
                      </div>
                      {previewMsgs.map((m, i) => (
                        <div key={i} className={`wxo-chat-row ${m.role}`}>
                          {m.role === 'bot' && widget.avatar && <img className="wxo-chat-avatar" src={widget.avatar} alt="" />}
                          <div className={`wxo-chat-msg ${m.role === 'user' ? 'wxo-chat-msg-user' : 'wxo-chat-msg-md'}`}
                            style={m.role === 'user' ? { background: widget.themeColor, borderColor: widget.themeColor } : undefined}>
                            {m.role === 'bot' ? <ReactMarkdown>{m.text}</ReactMarkdown> : m.text}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="wxo-chat-footer">
                      <WixQuestionEditor questions={widget.predefinedQuestions} onChange={(questions) => setStyle('predefinedQuestions', questions)} />
                      <div className="wxo-live-cta-row">
                        <button type="button" className="wxo-live-cta-btn" onClick={() => setLiveModalOpen(true)}>
                          <FontAwesomeIcon icon={faHeadset} /><span>{t('wxo_design_menu_human')}</span>
                        </button>
                      </div>
                      <div className="wxo-powered"><span className="wxo-powered-text">Powered by Ultimo Bots</span></div>
                      <div className="wxo-input-send">
                        <input className="wxo-chat-input" type="text" value={previewInput} placeholder={t('chat_input_placeholder')}
                          onChange={(e) => setPreviewInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendPreview(); } }} />
                        <button type="button" className="wxo-chat-send" style={{ backgroundColor: widget.themeColor, color: widget.headerColor }}
                          onClick={sendPreview} disabled={!previewInput.trim()}><FontAwesomeIcon icon={faArrowUp} /></button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {err && <div className="wxo-err">{err}</div>}
              {progressStrips}
            </div>
          )}

          {/* ── 3 — Test ── */}
          {step === STEP_TEST && (
            <div className="wxo-card wxo-card-test">
              {titleRow(<h2 className="wxo-h2">{t('wxo_test_title')}</h2>)}
              <p className="wxo-sub">{t('wpo_test_sub')}</p>
              {bot ? (
                <div className="wxo-test-chat">
                  <ChatModal embedded open onClose={() => {}} bot={{ id: bot, knowledge_count: 1 }}
                    currentMessageCount={0} messagePlanLimit={999999} planName="trial"
                    openSubscriptionModalFromChat={() => {}} onMessageCountChange={() => {}}
                    liveCtaAlways onLiveCta={() => setLiveModalOpen(true)} showImprove={false} />
                </div>
              ) : (
                <div className="wxo-scan"><div className="wxo-scan-ring" /><span>{t('wxo_test_preparing')}</span></div>
              )}
              {progressStrips}
            </div>
          )}

          {/* ── 4 — Add to the Webflow site ── */}
          {step === STEP_ADD && (
            <div className="wxo-card">
              {titleRow(<h2 className="wxo-h2">{t('wf_add_step_title')}</h2>)}
              <p className="wxo-sub">{t('wfo_add_text')}</p>
              <div className={`wfo-add-site ${liveOnSite ? 'wfo-added' : ''}`}>
                <span className="wfo-add-logo"><img src="/webflow-logo.png" alt="" /></span>
                <span className="wfo-add-text">
                  <strong>{siteName}</strong>
                  <span>{siteHost(site?.url)}</span>
                </span>
                {liveOnSite && <span className="wfo-add-done" role="status"><FontAwesomeIcon icon={faCircleCheck} /> {t('wfo_live')}</span>}
                {!liveOnSite && addPhase && (
                  <span className="wfo-add-busy" role="status">
                    <FontAwesomeIcon icon={faSpinner} spin /> {t(addPhase === 'adding' ? 'wfo_add_busy_adding' : 'wfo_add_busy_publishing')}
                  </span>
                )}
              </div>
              {/* No skip line in the normal view (Robert 04.11.); only when Webflow refuses, "Skip for now" next to the
                  error keeps the onboarding from being a dead end (the portal's Deploy tab can add it later). */}
              {addError && (
                <div className="wxo-err" role="alert">
                  {addError}{' '}
                  <button type="button" className="wfo-skip" onClick={() => setStep(STEP_NOTIFY)} disabled={Boolean(addPhase)}>{t('wfo_add_skip')}</button>
                </div>
              )}
              {progressStrips}
            </div>
          )}

          {/* ── 5 — Notifications ── */}
          {step === STEP_NOTIFY && (
            <div className="wxo-card">
              {titleRow(<h2 className="wxo-h2">{t('wxo_notif_title')}</h2>)}
              <p className="wxo-sub">{t('wxo_notif_sub')}</p>
              <div className="wxo-notify-matrix">
                <div className="wxo-notify-row wxo-notify-head">
                  <div className="wxo-notify-event-col" />
                  {WFO_NOTIFY_CHANNELS.map((ch) => (
                    <div key={ch.key} className="wxo-notify-channel-col">
                      <FontAwesomeIcon icon={ch.icon} className="wxo-notify-channel-icon" />
                      <span className="wxo-notify-channel-label">{t(ch.labelKey)}</span>
                    </div>
                  ))}
                </div>
                {WFO_NOTIFY_EVENTS.map((ev) => {
                  const row = notifyPrefs[ev.key] || {};
                  return (
                    <div key={ev.key} className={`wxo-notify-row ${ev.highlight ? 'wxo-notify-row-hi' : ''}`}>
                      <div className="wxo-notify-event-col">
                        <div className="wxo-notify-event-title"><FontAwesomeIcon icon={ev.icon} className="wxo-notify-event-icon" />{t(ev.titleKey)}</div>
                        <div className="wxo-notify-event-desc">{t(ev.descKey)}</div>
                      </div>
                      {WFO_NOTIFY_CHANNELS.map((ch) => {
                        const locked = wfoChannelLocked(ev.key, ch.key);
                        const checkbox = (
                          <Checkbox checked={locked ? false : Boolean(row[ch.key])} disabled={locked}
                            onChange={() => {
                              setNotifySaveAttempted(false);
                              setNotifyPrefs((prev) => ({ ...prev, [ev.key]: { ...(prev[ev.key] || {}), [ch.key]: !prev[ev.key]?.[ch.key] } }));
                            }}
                            sx={{ '&.Mui-checked': { color: '#5616ea' }, '&:hover': { backgroundColor: '#5616ea15' }, '&.Mui-disabled': { color: '#e0e0e6' }, padding: '4px' }} />
                        );
                        return (
                          <div key={ch.key} className="wxo-notify-channel-col">
                            {locked ? (
                              <Tooltip title={t('wxo_notif_locked')} arrow><span style={{ cursor: 'not-allowed', display: 'inline-flex' }}><span style={{ pointerEvents: 'none' }}>{checkbox}</span></span></Tooltip>
                            ) : checkbox}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
              <div className="wxo-notify-portal">
                <span className="wxo-notify-portal-label">{t('wxo_notif_also')}</span>
                <span className="wxo-notify-chips">
                  <span className="wxo-notify-chip"><TelegramBrandIcon size={15} />Telegram</span>
                  <span className="wxo-notify-chip"><SlackBrandIcon size={15} />Slack</span>
                  <span className="wxo-notify-chip"><TeamsBrandIcon size={15} />Teams<span className="wxo-notify-soon">{t('wxo_notif_soon')}</span></span>
                </span>
              </div>
              {notifyEmailRequired && (
                <div className="wxo-notify-recipients">
                  <div className="wxo-notify-recipients-head"><FontAwesomeIcon icon={faEnvelope} style={{ color: '#5616ea' }} /><span>{t('wxo_notif_recipients')}</span></div>
                  <div className="wxo-notify-emails">
                    {(notificationEmails.length ? notificationEmails : ['']).map((value, idx) => {
                      const list = notificationEmails.length ? notificationEmails : [''];
                      const trimmed = String(value).trim();
                      const showError = notifySaveAttempted && trimmed.length > 0 && !EMAIL_RE.test(trimmed);
                      return (
                        <div key={idx} className="wxo-notify-email-row">
                          <TextField label={t('wxo_notif_email_n', { n: idx + 1 })} value={value}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (idx === 0) notifyEmailUserEditedRef.current = true;
                              setNotifySaveAttempted(false);
                              setNotificationEmails((prev) => { const next = prev.slice(); next[idx] = v; return next; });
                            }}
                            type="text" fullWidth variant="outlined" size="small" error={showError} placeholder="you@example.com"
                            InputProps={{ endAdornment: list.length > 1 ? (<InputAdornment position="end"><IconButton aria-label="Remove" edge="end" size="small" onClick={() => { setNotifySaveAttempted(false); setNotificationEmails((prev) => { const next = prev.filter((_, i) => i !== idx); return next.length ? next : ['']; }); }}><FontAwesomeIcon icon={faTrash} style={{ fontSize: 13 }} /></IconButton></InputAdornment>) : null }}
                            sx={FIELD_SX} />
                        </div>
                      );
                    })}
                  </div>
                  {notificationEmails.length < 3 && (
                    <button type="button" className="wxo-notify-add-email" onClick={() => { setNotifySaveAttempted(false); setNotificationEmails((prev) => (prev.length >= 3 ? prev : [...prev, ''])); }} title={t('wxo_notif_add_email')}>
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  )}
                  {notifySaveAttempted && notifyEmailHasError && (
                    <div className="wxo-notify-error"><FontAwesomeIcon icon={faTriangleExclamation} /><span>{notifyEmailMissing ? t('wxo_notif_err_missing') : t('wxo_notif_err_invalid')}</span></div>
                  )}
                </div>
              )}
              {err && <div className="wxo-err">{err}</div>}
            </div>
          )}

          {/* ── 6 — Plan: 7-day free trial through Stripe, styled like /billing ── */}
          {step === STEP_PLAN && (
            <div className="wxo-card wxo-pricing">
              {trialStarted ? (
                <div className="wxo-trial-done">
                  <div className="wxo-trial-done-ic"><FontAwesomeIcon icon={faCheck} /></div>
                  <h2 className="wxo-h2">{t('wxo_price_done_title')}</h2>
                  <p className="wxo-sub">{t('wxo_price_done_sub')}</p>
                  <button className="wxo-btn" onClick={goToPortal}>{t('wxo_price_to_portal')} <FontAwesomeIcon icon={faArrowRight} /></button>
                </div>
              ) : isCheckingPlan ? (
                <div className="wxo-trial-wait">
                  <div className="wxo-scan-ring" />
                  <h2 className="wxo-h2">{t('wxo_price_detecting_title')}</h2>
                  <p className="wxo-sub">{t('wxo_price_detecting_sub')}</p>
                  {planCheckError && <div className="wxo-err">{planCheckError}</div>}
                  <div className="wxo-trial-wait-actions">
                    <button className="wxo-btn-ghost" onClick={stopPlanPolling}>{t('wxo_price_back')}</button>
                    <button className="wxo-btn" onClick={async () => { try { const n = await fetchBotPlanName(); if (n !== 'Free') { stopPlanPolling(); setTrialStarted(true); } else { setPlanCheckError(t('wxo_price_still_checking')); } } catch { setPlanCheckError(t('wxo_price_still_checking')); } }}>{t('wxo_price_refresh')}</button>
                  </div>
                </div>
              ) : (
                <>
                  {titleRow(<h2 className="wxo-h2">{t('wxo_price_title')}</h2>)}
                  <div className="rk-billing-toggle">
                    <span className={`rk-billing-thumb${billingCycle === 'YEAR' ? ' right' : ''}`} aria-hidden />
                    <button type="button" className={billingCycle === 'MONTH' ? 'active' : ''} onClick={() => setBillingCycle('MONTH')}>{t('monthly')}</button>
                    <button type="button" className={billingCycle === 'YEAR' ? 'active' : ''} onClick={() => setBillingCycle('YEAR')}>{t('yearly')}</button>
                  </div>
                  {plansLoading && !pricingTiers.length ? (
                    <div className="wxo-trial-wait"><div className="wxo-scan-ring" /></div>
                  ) : (
                    <div className="rk-plans">
                      {pricingTiers.map((mRow) => {
                        const name = mRow.name;
                        const yRow = yearlyByName[name];
                        const row = billingCycle === 'YEAR' ? (yRow || mRow) : mRow;
                        const sv = pricingSavings(name);
                        const featured = name === WFO_FEATURED_TIER;
                        const rescrape = mRow.automatic_rescraping;
                        const removeBranding = !!mRow.remove_branding;
                        const canCheckout = Boolean(row?.stripe_subscription_plan_id);
                        return (
                          <div key={name} className={`rk-plan${featured ? ' featured' : ''}`}>
                            {featured && <span className="rk-plan-flag">{t('recommended_start')}</span>}
                            <div className="rk-plan-name">{name}</div>
                            {billingCycle === 'YEAR' && sv ? (
                              <div>
                                <div className="rk-plan-price">
                                  <s className="rk-plan-old">${sv.monthly}</s>
                                  <span className="rk-plan-arrow">→</span>
                                  ${sv.yearlyMonthly}
                                  <span className="rk-plan-save">−{sv.pct}%</span>
                                </div>
                                <div className="rk-plan-billed">{t('billing_per_month_billed_annually')}</div>
                              </div>
                            ) : (
                              <div className="rk-plan-price">${stripeDisplayPrice(row)}<span>{t('wxo_price_per_month')}</span></div>
                            )}
                            <div className="rk-plan-limits">
                              <div className="rk-plan-limit"><FontAwesomeIcon icon={faComments} /><span>{t('wxo_price_limit_messages')}</span><strong>{(mRow.message_limit ?? 0).toLocaleString('de-CH')}</strong></div>
                              <div className="rk-plan-limit"><FontAwesomeIcon icon={faBookOpen} /><span>{t('wxo_price_limit_knowledge')}</span><strong>{(mRow.document_limit ?? 0).toLocaleString('de-CH')}</strong></div>
                              <div className="rk-plan-limit"><FontAwesomeIcon icon={faPuzzlePiece} /><span>{t('wxo_price_limit_skills')}</span><strong>{mRow.skill_limit ?? 0}</strong></div>
                              <div className="rk-plan-limit"><FontAwesomeIcon icon={faSliders} /><span>{t('wxo_price_limit_behaviors')}</span><strong>{mRow.behavior_limit ?? 0}</strong></div>
                            </div>
                            <button type="button" className={`rk-plan-cta ${featured ? 'featured' : ''}`} disabled={isCheckingPlan || !canCheckout} onClick={() => startTrialCheckout(name)}>{t('start_free_trial')}</button>
                            <div className="rk-plan-div" />
                            <div className="rk-plan-sec"><span>{t('billing_section_apps')}</span></div>
                            <WfoLogoRow items={WFO_APPS} lead={WFO_APP_ANY} />
                            <div className="rk-plan-div" />
                            <div className="rk-plan-sec"><span>{t('billing_section_channels')}</span></div>
                            <WfoLogoRow items={WFO_CHANNELS} lead={WFO_CHANNEL_ANY} />
                            <div className="rk-plan-div" />
                            <ul className="rk-plan-feats">
                              <li><FontAwesomeIcon icon={faCheck} className="rk-feat-ok" /> {t('wxo_price_feat_lead')}</li>
                              <li className={rescrape !== 'none' ? '' : 'off'}><FontAwesomeIcon icon={rescrape !== 'none' ? faCheck : faMinus} className={rescrape !== 'none' ? 'rk-feat-ok' : ''} /> {rescrape === 'daily' ? t('wxo_price_feat_rescrape_daily') : rescrape === 'monthly' ? t('wxo_price_feat_rescrape_monthly') : t('wxo_price_feat_rescrape')}</li>
                              <li className={removeBranding ? '' : 'off'}><FontAwesomeIcon icon={removeBranding ? faCheck : faMinus} className={removeBranding ? 'rk-feat-ok' : ''} /> {t('wxo_price_feat_branding')}</li>
                            </ul>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {err && <div className="wxo-err">{err}</div>}
                  <p className="wxo-price-note">{t('cancel_note')}</p>
                  <p className="wxo-price-free"><button type="button" onClick={goToPortal}>{t('wxo_price_free_link')}</button></p>
                </>
              )}
            </div>
          )}

          {stageCta && (
            <div className="wxo-stage-cta">
              <button type="button" className="wxo-cta-below" onClick={stageCta.onClick} disabled={stageCta.busy || stageCta.disabled}>
                <span>{stageCta.label}</span>
                {stageCta.busy ? <span className="wxo-spin" /> : <FontAwesomeIcon icon={faArrowRight} />}
              </button>
            </div>
          )}
        </div>
      </div>

      <UploadFileModal open={docModalOpen} onClose={() => setDocModalOpen(false)} onSubmit={handleDocsAdded} />

      {liveModalOpen && (
        <div className="wxo-live-modal-overlay" onClick={() => setLiveModalOpen(false)}>
          <div className="wxo-live-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="wxo-skills-grid-bg" aria-hidden="true" />
            <div className="wxo-skills-glow" aria-hidden="true" />
            <button type="button" className="wxo-live-modal-close" onClick={() => setLiveModalOpen(false)} aria-label={t('close')}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
            <div className="wxo-live-modal-inner">
              <span className="wxo-live-modal-icon"><FontAwesomeIcon icon={faHeadset} /></span>
              <h3 className="wxo-live-modal-title">{t('wxo_live_modal_title')}</h3>
              <p className="wxo-live-modal-body">{t('wxo_live_modal_body')}</p>
              <button type="button" className="wxo-live-modal-ok" onClick={() => setLiveModalOpen(false)}>{t('wxo_live_modal_ok')}</button>
            </div>
          </div>
        </div>
      )}
    </>,
  );
}

